import type { Pos, FuelGrid, GamePhase } from "../types.ts";
import { gridRows, gridCols, getFuel, getNeighbors } from "../engine/grid.ts";
import {
  fuelColor, fuelTextColor, PLAYER_COLOR, PLAYER_GLOW,
  VALID_MOVE_COLOR, GRID_LINE_COLOR, BURNED_PATTERN_COLOR,
} from "./colors.ts";

interface Layout {
  cellSize: number;
  offsetX: number;
  offsetY: number;
  rows: number;
  cols: number;
}

export function computeLayout(grid: FuelGrid, width: number, height: number): Layout {
  const rows = gridRows(grid);
  const cols = gridCols(grid);
  const maxCellW = Math.floor((width - 20) / cols);
  const maxCellH = Math.floor((height - 20) / rows);
  const cellSize = Math.min(maxCellW, maxCellH, 80);
  const gridW = cols * cellSize;
  const gridH = rows * cellSize;
  const offsetX = Math.floor((width - gridW) / 2);
  const offsetY = Math.floor((height - gridH) / 2);
  return { cellSize, offsetX, offsetY, rows, cols };
}

export function drawGrid(
  ctx: CanvasRenderingContext2D,
  grid: FuelGrid,
  playerPos: Pos | null,
  phase: GamePhase,
  width: number,
  height: number,
): void {
  const layout = computeLayout(grid, width, height);
  const { cellSize, offsetX, offsetY, rows, cols } = layout;

  ctx.clearRect(0, 0, width, height);

  // Valid move highlights
  const validMoves = new Set<string>();
  if (playerPos && (phase === "playing")) {
    for (const n of getNeighbors(grid, playerPos)) {
      if (getFuel(grid, n) > 0) validMoves.add(`${n.row},${n.col}`);
    }
  }

  // Draw cells
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = offsetX + c * cellSize;
      const y = offsetY + r * cellSize;
      const fuel = getFuel(grid, { row: r, col: c });

      // Cell background
      ctx.fillStyle = fuelColor(fuel);
      ctx.fillRect(x + 1, y + 1, cellSize - 2, cellSize - 2);

      // Burned out cross-hatch
      if (fuel === 0) {
        ctx.strokeStyle = BURNED_PATTERN_COLOR;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x + 4, y + 4);
        ctx.lineTo(x + cellSize - 4, y + cellSize - 4);
        ctx.moveTo(x + cellSize - 4, y + 4);
        ctx.lineTo(x + 4, y + cellSize - 4);
        ctx.stroke();
      }

      // Valid move highlight
      if (validMoves.has(`${r},${c}`)) {
        ctx.fillStyle = VALID_MOVE_COLOR;
        ctx.fillRect(x + 1, y + 1, cellSize - 2, cellSize - 2);

        // Pulsing border
        ctx.strokeStyle = PLAYER_COLOR;
        ctx.lineWidth = 2;
        ctx.strokeRect(x + 2, y + 2, cellSize - 4, cellSize - 4);
      }

      // Starting phase: highlight all clickable tiles
      if (phase === "starting" && fuel > 0) {
        ctx.strokeStyle = "rgba(249, 115, 22, 0.3)";
        ctx.lineWidth = 1;
        ctx.strokeRect(x + 2, y + 2, cellSize - 4, cellSize - 4);
      }

      // Fuel number
      if (fuel > 0) {
        ctx.fillStyle = fuelTextColor(fuel);
        ctx.font = `bold ${Math.floor(cellSize * 0.4)}px -apple-system, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(fuel), x + cellSize / 2, y + cellSize / 2);
      }
    }
  }

  // Grid lines
  ctx.strokeStyle = GRID_LINE_COLOR;
  ctx.lineWidth = 1;
  for (let r = 0; r <= rows; r++) {
    ctx.beginPath();
    ctx.moveTo(offsetX, offsetY + r * cellSize);
    ctx.lineTo(offsetX + cols * cellSize, offsetY + r * cellSize);
    ctx.stroke();
  }
  for (let c = 0; c <= cols; c++) {
    ctx.beginPath();
    ctx.moveTo(offsetX + c * cellSize, offsetY);
    ctx.lineTo(offsetX + c * cellSize, offsetY + rows * cellSize);
    ctx.stroke();
  }

  // Player marker
  if (playerPos && (phase === "playing" || phase === "won" || phase === "stuck")) {
    const px = offsetX + playerPos.col * cellSize + cellSize / 2;
    const py = offsetY + playerPos.row * cellSize + cellSize / 2;
    const radius = cellSize * 0.3;

    // Glow
    ctx.beginPath();
    ctx.arc(px, py, radius + 4, 0, Math.PI * 2);
    ctx.fillStyle = PLAYER_GLOW;
    ctx.fill();

    // Player circle
    ctx.beginPath();
    ctx.arc(px, py, radius, 0, Math.PI * 2);
    ctx.fillStyle = PLAYER_COLOR;
    ctx.fill();

    // Inner highlight
    ctx.beginPath();
    ctx.arc(px - radius * 0.2, py - radius * 0.2, radius * 0.4, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
    ctx.fill();
  }
}

export function hitTest(
  grid: FuelGrid,
  width: number,
  height: number,
  clickX: number,
  clickY: number,
): Pos | null {
  const layout = computeLayout(grid, width, height);
  const { cellSize, offsetX, offsetY, rows, cols } = layout;

  const col = Math.floor((clickX - offsetX) / cellSize);
  const row = Math.floor((clickY - offsetY) / cellSize);

  if (row < 0 || row >= rows || col < 0 || col >= cols) return null;
  return { row, col };
}
